import {
  LuActivity, LuAlarmClock, LuBarChart3, LuBell, LuBellRing, LuBookOpen, LuBot, LuBrainCircuit,
  LuClapperboard, LuCode2, LuContact, LuFileBarChart, LuFileCode, LuFileText, LuFilter,
  LuFootprints, LuGauge, LuGitBranch, LuInbox, LuKeyRound, LuLayers, LuLayoutDashboard,
  LuLayoutTemplate, LuLifeBuoy, LuLineChart, LuLink, LuLink2, LuMail, LuMegaphone,
  LuMessageSquare, LuMessagesSquare, LuNetwork, LuPieChart, LuReceipt, LuFileSearch, LuScrollText,
  LuSend, LuSettings, LuShieldCheck, LuSmartphone, LuSplit, LuTarget, LuTicket, LuTimer,
  LuToggleRight, LuUserCog, LuUsers, LuWebhook, LuFile, LuFolder,
} from 'react-icons/lu';

/**
 * Icon + one-line description for every nav entry, keyed by the backend page
 * `slug` (the same slug the nav links to as `/${slug}`; groups use their own
 * slugs, see PARENT_NAV_SLUGS in Header.js).
 *
 * This is the frontend fallback until the backend `pages` table carries the
 * data itself. Once a page row has them, the backend wins — see getNavMeta():
 *
 *   icon:        string, one of the keys in NAV_ICONS below, e.g. "LuMegaphone"
 *   description: string, short (≈ 60 chars), shown under the name in the menu
 *
 * Groups (dropdown parents) also feed the mega-menu feature card:
 *
 *   headline:    string, one short sentence for the card
 *   cta:         { label, slug } — the card's button. Only used when `slug` is one
 *                of the pages this user can see in that group; otherwise the card
 *                links to the group's first page.
 *
 * Missing, blank or unknown values never break the nav: each field falls back to
 * this map, then to a default (folder / page icon, "Explore <name>" / "Open <name>").
 * See getNavMeta().
 */

/**
 * Icons the backend may reference by name. Only icons listed here are bundled —
 * importing all of react-icons/lu to resolve arbitrary names would add the whole
 * icon set to the bundle. Add an entry here when the backend needs a new one.
 */
export const NAV_ICONS = {
  LuActivity, LuAlarmClock, LuBarChart3, LuBell, LuBellRing, LuBookOpen, LuBot, LuBrainCircuit,
  LuClapperboard, LuCode2, LuContact, LuFileBarChart, LuFileCode, LuFileText, LuFilter,
  LuFootprints, LuGauge, LuGitBranch, LuInbox, LuKeyRound, LuLayers, LuLayoutDashboard,
  LuLayoutTemplate, LuLifeBuoy, LuLineChart, LuLink, LuLink2, LuMail, LuMegaphone,
  LuMessageSquare, LuMessagesSquare, LuNetwork, LuPieChart, LuReceipt, LuFileSearch, LuScrollText,
  LuSend, LuSettings, LuShieldCheck, LuSmartphone, LuSplit, LuTarget, LuTicket, LuTimer,
  LuToggleRight, LuUserCog, LuUsers, LuWebhook,
};

// Defaults when neither the API nor NAV_META has an icon for the node.
export const DEFAULT_NAV_ICON = LuFile;
export const DEFAULT_GROUP_ICON = LuFolder;

export const NAV_META = {
  // ── Groups (dropdown parents) ────────────────────────────────────────────
  'contact-aud': {
    icon: 'LuUsers', description: 'Contacts, segments and audience insight',
    headline: 'Know every customer, reach the right ones.',
  },
  'campaigns-ourneys': {
    icon: 'LuMegaphone', description: 'Campaigns, flows and automated journeys',
    headline: 'Plan, launch and automate every message.',
    cta: { label: 'Launch a campaign', slug: 'marketing/campaign' },
  },
  'content': {
    icon: 'LuLayers', description: 'Templates, designs, links and reminders',
    headline: 'Build once, reuse it on every channel.',
    cta: { label: 'Design an email', slug: 'tools/email-templates' },
  },
  'settings': {
    icon: 'LuSettings', description: 'Account, users, routing and configuration',
    headline: 'Set up the workspace the way your team works.',
  },
  'message-logs-main': {
    icon: 'LuScrollText', description: 'Delivery, webhook and reminder logs',
    headline: 'See what was sent and what happened next.',
    cta: { label: 'Open delivery logs', slug: 'message-logs' },
  },
  'reports': {
    icon: 'LuFileBarChart', description: 'Usage and delivery reporting',
    headline: 'Know what you sent and what it cost.',
  },
  'analytics': {
    icon: 'LuLineChart', description: 'Funnels, journeys, boards and metrics',
    headline: 'Turn events into decisions.',
    cta: { label: 'Build a funnel', slug: 'analytics/funnels' },
  },
  'documentation': {
    icon: 'LuBookOpen', description: 'APIs, SDKs and integration guides',
    headline: 'Everything you need to integrate.',
    cta: { label: 'Read the API docs', slug: 'documentation/api-doc' },
  },
  'user-management': {
    icon: 'LuUserCog', description: 'Users, roles and permissions',
    headline: 'Control who can do what.',
  },
  'support': {
    icon: 'LuLifeBuoy', description: 'Help and support tickets',
    headline: "We're here when you need us.",
  },

  // ── Main ─────────────────────────────────────────────────────────────────
  'dashboard':          { icon: 'LuLayoutDashboard', description: 'Account performance, usage and savings' },
  'contacts':           { icon: 'LuContact',        description: 'Manage contacts, tags and cohorts' },
  'template-manager':   { icon: 'LuLayoutTemplate', description: 'Notification templates for every channel' },
  'billing':            { icon: 'LuReceipt',        description: 'Invoices, charges and payments' },
  'routing-methods':    { icon: 'LuSplit',          description: 'How each message type is routed' },
  'send-message':       { icon: 'LuSend',           description: 'Send a real test message via the API' },
  'incomming-sms':      { icon: 'LuInbox',          description: 'Incoming SMS from your customers' },

  // ── Campaigns & journeys / marketing ─────────────────────────────────────
  'marketing/campaign':         { icon: 'LuMegaphone',      description: 'Launch and track bulk campaigns' },
  'marketing/cohorts':          { icon: 'LuPieChart',       description: 'Build dynamic user segments' },
  'marketing/event':            { icon: 'LuActivity',       description: 'Every event tracked across sources' },
  'flow':                       { icon: 'LuGitBranch',      description: 'Automated customer journey flows' },
  'marketing/session-analyzer': { icon: 'LuTimer',          description: 'Trigger flows when sessions end' },
  'marketing/rfm-models':       { icon: 'LuGauge',          description: 'Recency, frequency and monetary models' },
  'marketing/smart-links':      { icon: 'LuLink2',          description: 'Short links with tracking and routing' },
  'marketing/story-builder':    { icon: 'LuClapperboard',   description: 'Create and track AMP Web Stories' },
  'marketing/asklytics':        { icon: 'LuMessagesSquare', description: 'Ask questions about your data' },

  // ── Content / tools ──────────────────────────────────────────────────────
  'tools/email-templates':      { icon: 'LuMail',           description: 'Saved emails and reusable templates' },
  'tools/in-app-messages':      { icon: 'LuBell',           description: 'In-app modals, banners and prompts' },
  'content/deep-link-library':  { icon: 'LuLink',           description: 'App deep links in one place' },
  'content/reminders':          { icon: 'LuAlarmClock',     description: 'Reminders the phone schedules itself' },
  'tools/csv-message-sender':   { icon: 'LuFileText',       description: 'Bulk send from a CSV file' },
  'tools/safe-message-filter':  { icon: 'LuShieldCheck',    description: 'Keyword rules for compliant messages' },
  'tools/sms-group':            { icon: 'LuBrainCircuit',   description: 'Real-time analysis of incoming SMS' },
  'tools/conversion-events':    { icon: 'LuTarget',         description: 'Events that count as business success' },
  'parser':                     { icon: 'LuFileSearch',       description: 'Parse and extract message content' },

  // ── Logs ─────────────────────────────────────────────────────────────────
  'message-logs':               { icon: 'LuScrollText',     description: 'Every message and its delivery status' },
  'webhook':                    { icon: 'LuWebhook',        description: 'Webhook calls and their responses' },
  'on-device-reminder-logs':    { icon: 'LuBellRing',       description: 'Reminders served and clicked' },

  // ── Analytics / reports ──────────────────────────────────────────────────
  'analytics/usage-report':     { icon: 'LuBarChart3',      description: 'Message usage by channel and type' },
  'analytics/funnels':          { icon: 'LuFilter',         description: 'Conversion funnels and drop-off' },
  'analytics/session-journey':  { icon: 'LuFootprints',     description: 'Step-by-step user sessions' },
  'analytics/insights':         { icon: 'LuLayoutDashboard', description: 'Custom boards of charts and KPIs' },
  'analytics/metric-tree':      { icon: 'LuNetwork',        description: 'How your metrics relate to each other' },

  // ── Users ────────────────────────────────────────────────────────────────
  'user-management/user':             { icon: 'LuUsers',    description: 'User accounts and two-factor auth' },
  'user-management/roles-permission': { icon: 'LuKeyRound', description: 'Roles and what each can access' },

  // ── Documentation ────────────────────────────────────────────────────────
  'documentation/api-doc':                     { icon: 'LuCode2',       description: 'Send messages through one API' },
  'documentation/configure-sdk':               { icon: 'LuSmartphone',  description: 'Integrate the mobile SDK' },
  'documentation/evt-docs':                    { icon: 'LuActivity',    description: 'Track events from any source' },
  'documentation/contact-api':                 { icon: 'LuContact',     description: 'Create contacts with one request' },
  'documentation/channel-opt':                 { icon: 'LuToggleRight', description: 'Opt contacts in or out of channels' },
  'documentation/webhook-doc':                 { icon: 'LuWebhook',     description: 'Receive delivery updates by webhook' },
  'documentation/configure-push-notification': { icon: 'LuBell',        description: 'Set up Firebase push for Android' },
  'documentation/configure-apn':               { icon: 'LuBell',        description: 'Set up Apple Push Notifications' },
  'message-api-doc':                           { icon: 'LuFileCode',    description: 'Message API reference' },
  'web-hook':                                  { icon: 'LuWebhook',     description: 'Webhook reference' },

  // ── Support ──────────────────────────────────────────────────────────────
  'support/tickets':            { icon: 'LuTicket',         description: 'Your support tickets' },
};

const normalizeSlug = (slug) => String(slug || '').replace(/(?:^\/+)|(?:\/+$)/g, '').replace(/\/\*$/, '').toLowerCase();

/** A usable string from the API: null, undefined, non-strings and blank/whitespace all count as "not sent". */
const textOrNull = (value) => (typeof value === 'string' && value.trim() ? value.trim() : null);

const isGroup = (node) => Array.isArray(node?.children) && node.children.length > 0;

/** Last-resort defaults, for a page that neither the API nor NAV_META knows about. */
export const defaultNavMeta = (node) => {
  const name = textOrNull(node?.name);
  return {
    icon: isGroup(node) ? DEFAULT_GROUP_ICON : DEFAULT_NAV_ICON,
    description: name ? (isGroup(node) ? `Explore ${name}` : `Open ${name}`) : '',
  };
};

/**
 * Icon component + description (and group card copy) for a nav node.
 * Each field is resolved on its own, in this order:
 *   1. the API value on the node, if it's a non-blank string (and, for `icon`, a
 *      name listed in NAV_ICONS);
 *   2. the NAV_META entry for the node's slug;
 *   3. a default — a folder icon for groups / a page icon for pages, and
 *      "Explore <name>" / "Open <name>".
 */
export const getNavMeta = (node) => {
  const fallback = NAV_META[normalizeSlug(node?.slug)] || {};
  const defaults = defaultNavMeta(node);
  return {
    icon: NAV_ICONS[textOrNull(node?.icon)] || NAV_ICONS[fallback.icon] || defaults.icon,
    description: textOrNull(node?.description) || fallback.description || defaults.description,
    headline: textOrNull(node?.headline) || fallback.headline || '',
    cta: (node?.cta && textOrNull(node.cta.slug) && textOrNull(node.cta.label) ? node.cta : null) || fallback.cta || null,
  };
};
